import { useEffect, useState } from "react";
import { VinoDataOffer } from "../../types/vino-data-types";
import isEqual from "lodash.isequal";
import styled from "styled-components";
import { CartItem } from "../../contexts/cart/types";
import {
  Box,
  FlexGrid,
  Heading,
  Row,
  Section,
  Stack,
} from "@vinomofo/components";
import hasAnalytics from "../../utils/has-analytics";
import { RelatedOffersList } from "./RelatedOffersList";
import vinoFetch from "../../utils/vinoFetch";
import { useLoyalty } from "../../hooks/account/use-loyalty";
import { RelatedItemsProps } from "./types";

const StyledSection = styled(Section)`
  background-color: #fff;
  padding-bottom: 40px;
  > div > div > div {
    overflow: inherit;
  }

  @media (max-width: 1160px) {
    background-color: ${props => props.backgroundColor || "#fff"};
  }
`;

export const RelatedSimilarItems = (props: RelatedItemsProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const [recommendedOffers, setRecommendedOffers] = useState<VinoDataOffer[]>(
    []
  );

  const { loyalty } = useLoyalty();

  const {
    offerId,
    userId,
    heading,
    skuCodes,
    isSpirit,
    hidden,
    backgroundColor,
  } = props;

  useEffect(() => {
    const fetchRecommendedSimilarOffers = async (
      offerId?: string,
      userId?: string
    ) => {
      setIsLoading(true);
      let anonymousUserId = null;
      const marketPrefix =
        process.env.GATSBY_VM_MARKET === "SG" ? "SG-" : "AU-";
      if (!userId && hasAnalytics()) {
        const analytics = window?.analytics;
        anonymousUserId =
          analytics?.user?.()?.anonymousId() || `${marketPrefix}0`;
      }
      const finalUserId = userId ? `${marketPrefix}${userId}` : anonymousUserId;
      try {
        const endpointSlug =
          isSpirit || hidden ? "get-others-you-may-like" : "get-similar-items";

        const baseUrl = `/recommendation/api/${endpointSlug}`;

        const params = new URLSearchParams({
          id: offerId || "",
          userId: finalUserId,
        });

        if (loyalty?.loyalty_tier) {
          params.append("tiersEligible", loyalty.loyalty_tier?.toUpperCase());
        }
        if (skuCodes) {
          params.append("skuCodes", skuCodes.join(","));
        }
        params.append("market", process.env.GATSBY_VM_MARKET || "AU");
        params.append("env", process.env.GATSBY_VM_ENV || "staging");
        const url = `${baseUrl}?${params.toString()}`;
        const response = await vinoFetch.get(url);
        if (!isEqual(recommendedOffers, response.data)) {
          setRecommendedOffers(response.data);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error("Error fetching offers:", error);
      }
    };

    if (offerId || userId) {
      fetchRecommendedSimilarOffers(offerId, userId);
    }
  }, [offerId, userId, loyalty]);

  return (
    <StyledSection>
      <Box width="100%">
        <FlexGrid gutter={1} display={["block"]}>
          {heading && (
            <Stack space={2}>
              {heading && (
                <Heading as="h3" variant="lg" textAlign="center">
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    padding={["40px"]}
                  >
                    {heading}
                  </Box>
                </Heading>
              )}
            </Stack>
          )}
          <RelatedOffersList
            backgroundColor={backgroundColor}
            recommendedOffers={recommendedOffers}
            isLoading={isLoading}
            isShoppingCartPage={false}
            engine={"vertex"}
            {...props}
          />
        </FlexGrid>
      </Box>
    </StyledSection>
  );
};
